import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Button } from 'primereact/button';
import { ApiContext } from '@api/api';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ButtonRow, RingilHeading } from '@components/styles.tsx';
import { MultipleExpeditionDetailV4OutDTO } from '@api/ringil-api.v2.tsx';
import { isNotBlank } from '@utils/utils.tsx';
import SvgWarning from '@icons/alert.svg?react';

/** dachser.xsd: CustomerShipmentReference is xs:string with maxLength 35 — BE cuts anything longer (RD-2916) */
export const DACHSER_SHIPMENT_REFERENCE_MAX_LENGTH = 35;

/** Mirrors DachserExportService.buildCustomerShipmentReference: applicationId + all location references, comma separated */
export const buildDachserShipmentReference = (detail: MultipleExpeditionDetailV4OutDTO): string =>
  [detail.applicationId, ...(detail.locations || []).map(l => l.loadingReference)]
    .filter(isNotBlank)
    .map(r => `${r}`.trim())
    .filter(isNotBlank)
    .join(', ');

interface ExpeditionInfo {
  applicationId: string;
  expeditionId: string;
  fromLocation: string;
  toLocation: string;
}

interface ComponentProps {
  onComplete: () => void;
  selectedExpeditions: ExpeditionInfo[];
}

export const useDachserExport = () => {
  const [visible, setVisible] = useState(false);
  // Kept at hook level: the inner component is re-created on every parent render, so component-level state would reset.
  const [referenceByExpeditionId, setReferenceByExpeditionId] = useState<Record<string, string>>({});

  const MassActionDachser: React.FC<ComponentProps> = ({ selectedExpeditions, onComplete }) => {
    const { tr } = useTranslationLgs();
    const { generateDachserExportAndDownload, downloadAttachment, getMultipleExpeditionDetails } = useContext(ApiContext);

    const expeditionIdsKey = selectedExpeditions.map(t => t.expeditionId).join(',');
    useEffect(() => {
      if (!visible || selectedExpeditions.length === 0) return;
      if (selectedExpeditions.every(t => t.expeditionId in referenceByExpeditionId)) return;
      getMultipleExpeditionDetails(
        selectedExpeditions.map(t => t.expeditionId),
        details => setReferenceByExpeditionId(Object.fromEntries(details.map(d => [d.id, buildDachserShipmentReference(d)]))),
        null,
        { onError: error => console.error('Failed to load expedition details for Dachser export', error) },
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, expeditionIdsKey]);

    const renderReference = (row: ExpeditionInfo) => {
      const reference = referenceByExpeditionId[row.expeditionId];
      if (!reference) return null;
      const tooLong = reference.length > DACHSER_SHIPMENT_REFERENCE_MAX_LENGTH;
      return (
        <div className={'flex flex-col gap-1'}>
          <span className={'break-all'}>{reference}</span>
          {tooLong && (
            <span className={'flex flex-row gap-1 items-center text-complementary-warning font-medium'}>
              <SvgWarning className={'shrink-0'} />
              {tr('MassActionDachser.referenceTooLong', 'Reference exceeds {{max}} characters, Dachser will receive only the first {{max}}', {
                max: DACHSER_SHIPMENT_REFERENCE_MAX_LENGTH,
              })}
            </span>
          )}
        </div>
      );
    };

    const exportExpeditions = () => {
      generateDachserExportAndDownload({ expeditionIds: selectedExpeditions.map(t => t.expeditionId) }, d => {
        downloadAttachment(d.attachmentId, data => {
          // @ts-ignore
          const linkSource = `data:${data.mimeType};base64,${data.data}`;
          const downloadLink = document.createElement('a');

          downloadLink.href = linkSource;
          downloadLink.download = data.fileName;
          downloadLink.click();
          setVisible(false);
          onComplete();
        });
      });
    };

    return (
      <>
        {/*// @ts-ignore*/}
        <Dialog onHide={() => setVisible(false)} visible={visible} closeOnEscape={false} blockScroll={true} style={{ width: '800px' }}>
          <RingilHeading>{tr(`MassActionDachser.dachserExport`, `Dachser export`)}</RingilHeading>
          <SidebarContent>
            {/*// @ts-ignore*/}
            <DataTable value={selectedExpeditions} emptyMessage={tr(`global.noRecordFound`, `Nebyly nalezeny žádné záznamy`)}>
              <Column header={tr(`MassActionDachser.applicationId`, `applicationId`)} field={'applicationId'}></Column>
              <Column header={tr(`MassActionDachser.fromLocation`, `fromLocation`)} field={'fromLocation'}></Column>
              <Column header={tr(`MassActionDachser.toLocation`, `toLocation`)} field={'toLocation'}></Column>
              <Column header={tr(`MassActionDachser.shipmentReference`, `Dachser reference`)} body={renderReference}></Column>
            </DataTable>
            <ButtonRow>
              <Button label={tr(`RingilSidebar.export`, `Export`)} className="p-mr-2" onClick={e => exportExpeditions()} />
              <Button label={tr(`RingilSidebar.cancel`, `Cancel`)} className="p-button-text" onClick={() => onComplete()} />
            </ButtonRow>
          </SidebarContent>
        </Dialog>
      </>
    );
  };

  return { MassActionDachser, setVisible };
};

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
`;
